import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLingui } from "@lingui/react/macro";
import { useAuth } from "@/hooks/useAuth";
import { regionApi } from "@/lib/api/admin-api";
import {
  Search,
  UtensilsCrossed,
  MoreHorizontal,
  Info,
  CircleCheck,
  PlusCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import type { RegionRecipe, RecipeSource } from "@/types/recipe";

export interface RecipeListingSearchParams {
  search?: string;
  source?: "all" | "org-wide" | "region-share";
}

interface RecipeListingProps {
  searchParams: RecipeListingSearchParams;
  onNavigate: (
    updater: (prev: RecipeListingSearchParams) => RecipeListingSearchParams,
  ) => void;
  regionId: number;
}

export function RecipeListing({
  searchParams,
  onNavigate,
  regionId,
}: RecipeListingProps) {
  const { t } = useLingui();
  const { isAuthenticated } = useAuth();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const { data: recipes, isLoading } = useQuery({
    queryKey: [
      "region-recipes",
      regionId,
      searchParams.search,
      searchParams.source,
    ],
    queryFn: () =>
      regionApi.getRecipes(regionId, {
        search: searchParams.search,
        source: searchParams.source !== "all" ? searchParams.source : undefined,
      }),
    enabled: isAuthenticated,
  });

  const categories = useMemo(() => {
    if (!recipes) return [];
    return [
      ...new Set(recipes.map((r) => r.category).filter(Boolean) as string[]),
    ].sort();
  }, [recipes]);

  const filtered = useMemo(() => {
    if (!recipes) return [];
    if (!activeCategory) return recipes;
    return recipes.filter((r) => r.category === activeCategory);
  }, [recipes, activeCategory]);

  const sourceOptions: {
    value: "all" | "org-wide" | "region-share";
    label: string;
  }[] = [
    { value: "all", label: t`All` },
    { value: "org-wide", label: t`Org-wide` },
    { value: "region-share", label: t`Region share` },
  ];

  return (
    <div className="flex flex-col gap-6 px-4">
      {/* Section header */}
      <div className="flex flex-col gap-5 pb-px">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-2xl font-medium text-(--color-text-primary) leading-7">{t`Recipes`}</h2>
            <p className="text-sm text-(--color-text-secondary) leading-5">
              {t`View and manage recipes available in this region.`}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              className="h-10 rounded-[10px] px-4 border-(--shell-border) gap-2"
            >
              <CircleCheck className="size-4" />
              <span className="text-sm font-medium text-(--color-text-primary)">{t`Select`}</span>
            </Button>
            <Button className="h-11 rounded-[10px] px-4 bg-(--color-action) hover:bg-(--color-action)/90 text-(--color-action-fg) gap-2">
              <PlusCircle className="size-4" />
              <span className="text-sm font-medium">{t`Add recipe from Catalog`}</span>
            </Button>
          </div>
        </div>
        <div className="h-px bg-(--shell-border)" />
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 bg-(--color-surface-content) border border-(--shell-border) rounded-lg shadow-xs px-4 py-3 w-[300px]">
          <Search className="size-5 text-(--color-text-secondary) shrink-0" />
          <input
            type="text"
            placeholder={t`Search...`}
            value={searchParams.search ?? ""}
            onChange={(e) =>
              onNavigate((prev) => ({
                ...prev,
                search: e.target.value || undefined,
              }))
            }
            className="flex-1 text-base text-(--color-text-primary) placeholder:text-(--color-text-secondary) bg-transparent outline-none"
          />
        </div>

        <div className="flex items-center gap-3">
          {/* Source filter */}
          <div className="flex items-center gap-2">
            <span className="text-base text-(--color-text-secondary)">{t`Source:`}</span>
            <div className="flex items-center gap-1">
              <select
                value={searchParams.source ?? "all"}
                onChange={(e) =>
                  onNavigate((prev) => ({
                    ...prev,
                    source: e.target
                      .value as RecipeListingSearchParams["source"],
                  }))
                }
                className="text-base font-medium text-(--color-text-primary) bg-transparent outline-none cursor-pointer"
              >
                {sourceOptions.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Category tabs */}
      {categories.length > 0 && (
        <div className="flex items-center gap-1 px-1">
          <Button
            variant="ghost"
            type="button"
            onClick={() => setActiveCategory(null)}
            className={cn(
              "px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
              activeCategory === null
                ? "bg-(--color-action) text-(--color-action-fg) font-medium hover:bg-(--color-action) hover:text-(--color-action-fg)"
                : "text-(--color-text-secondary) hover:text-(--color-text-secondary) hover:bg-transparent",
            )}
          >
            {t`All`}
          </Button>
          {categories.map((cat) => (
            <Button
              key={cat}
              variant="ghost"
              type="button"
              onClick={() =>
                setActiveCategory(cat === activeCategory ? null : cat)
              }
              className={cn(
                "px-3 py-2 rounded-md text-sm transition-colors whitespace-nowrap",
                activeCategory === cat
                  ? "bg-(--color-action) text-(--color-action-fg) font-medium hover:bg-(--color-action) hover:text-(--color-action-fg)"
                  : "text-(--color-text-secondary) hover:text-(--color-text-secondary) hover:bg-transparent",
              )}
            >
              {cat}
            </Button>
          ))}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-[68px] w-full rounded-[10px]" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-16 text-center text-sm text-(--color-text-secondary)">
          {t`No recipes found`}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((recipe) => (
            <RecipeRow key={recipe.id} recipe={recipe} />
          ))}
        </div>
      )}
    </div>
  );
}

function RecipeRow({ recipe }: { recipe: RegionRecipe }) {
  const { t } = useLingui();

  const targetsLabel =
    recipe.targets === "all" ? t`All locations` : recipe.targets.join(", ");

  const SOURCE_TOOLTIPS: Record<RecipeSource, string> = {
    "org-wide":
      t`This recipe is available to all regions and all locations by default. Regions and locations may override it if allowed.`,
    "region-share":
      t`This recipe was added to the region from the Org catalog and is shared with all or selected locations, depending on region targets.`,
  };

  const SOURCE_LABELS: Record<RecipeSource, string> = {
    "org-wide": t`Org-wide`,
    "region-share": t`Region share`,
  };

  return (
    <div className="bg-(--color-surface-muted) border border-(--color-border) flex items-center justify-between p-3 rounded-[10px] w-full">
      {/* Image + name */}
      <div className="flex flex-1 gap-3 items-center min-w-0">
        <div className="size-[68px] shrink-0 rounded-[10px] border border-(--shell-border) bg-(--color-surface-content) flex items-center justify-center overflow-hidden">
          {recipe.imageUrl ? (
            <img
              src={recipe.imageUrl}
              alt={recipe.name}
              className="size-full object-cover"
            />
          ) : (
            <UtensilsCrossed className="size-7 text-(--color-text-secondary)" />
          )}
        </div>
        <div className="flex flex-col justify-center min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-base font-medium text-(--color-text-primary) whitespace-nowrap">
              {recipe.name}
            </span>
            {recipe.isPublished && (
              <Badge className="rounded-full px-2 py-0.5 text-xs font-medium border-transparent h-6 bg-(--brand-blue) text-(--brand-white) hover:bg-(--brand-blue)/90">
                {t`Published`}
              </Badge>
            )}
          </div>
          <span className="text-sm font-medium text-(--color-text-secondary) truncate">
            RCP-{recipe.id}
          </span>
        </div>
      </div>

      {/* Source */}
      <div className="flex flex-col gap-2 items-start justify-center w-[280px] shrink-0">
        <span className="text-sm text-(--color-text-secondary)">{t`Source:`}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-1.5 cursor-default">
              <span className="text-sm font-medium text-(--color-text-primary)">
                {SOURCE_LABELS[recipe.source]}
              </span>
              <Info className="size-4 text-(--color-text-secondary)" />
            </div>
          </TooltipTrigger>
          <TooltipContent className="max-w-[280px] text-center bg-(--color-action) text-(--color-action-fg) text-xs leading-relaxed">
            {SOURCE_TOOLTIPS[recipe.source]}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* Targets */}
      <div className="flex flex-col gap-2 items-start justify-center w-[280px] shrink-0">
        <span className="text-sm text-(--color-text-secondary)">{t`Targets:`}</span>
        <span className="text-sm font-medium text-(--color-text-primary)">
          {targetsLabel}
        </span>
      </div>

      {/* Actions */}
      <Button
        variant="brand-outline"
        type="button"
        className="size-11 shrink-0 p-0"
      >
        <MoreHorizontal className="size-5 text-(--color-text-secondary)" />
      </Button>
    </div>
  );
}
